import { MutableRefObject, useEffect, useRef } from "react";
import { Animated } from "react-native";
import {
  DraggingComponent,
  DropZone,
  Point,
} from "../../../utilities/drag-drop";
import { useScheduler } from "../../../utilities/scheduler";
import {
  CHECK_ZONES_INTERVAL,
  ENTER_SCHEDULE_DELAY,
  PAGE_CHANGE_DELAY,
} from "./constants";
import { CollisionResult, CollisionType } from "./types";
import { getCollisionResults } from "./utils";

type UseZonesArgs = {
  dropZonesRef: MutableRefObject<Map<string, DropZone>>;
  draggingComponentRef: MutableRefObject<DraggingComponent | null>;
  enteredZoneRef: MutableRefObject<string | null>;
  pointerRef: MutableRefObject<Animated.ValueXY>;
};

export const useZones = ({
  enteredZoneRef,
  pointerRef,
  dropZonesRef,
  draggingComponentRef,
}: UseZonesArgs) => {
  const { schedule } = useScheduler(ENTER_SCHEDULE_DELAY);

  const pageBarLockRef = useRef<boolean>(false);

  useEffect(() => {
    const a = setInterval(scheduleCheckZones, CHECK_ZONES_INTERVAL);
    return () => {
      clearInterval(a);
    };
  }, []);

  function scheduleCheckZones() {
    if (!draggingComponentRef.current) return;
    const position = JSON.parse(JSON.stringify(pointerRef.current));
    const offsetPosition = {
      x: position.x,
      y: position.y,
    };

    // if we are performing shift/restore, don't check zones yet
    checkZones(offsetPosition);
  }

  /**
   * Leaves previously entered zone and enters new zones if position is within their bounds
   * @param position pointer position
   */
  function checkZones(position: Point) {
    const collisionResults = getCollisionResults(
      [...dropZonesRef.current.values()],
      position
    );

    const targetId = enteredZoneRef.current;

    if (targetId) {
      const c = collisionResults.filter(
        ({ hit, zone }) => !hit && zone.id === targetId
      );

      c.forEach(({ zone, type }) => {
        if (type === CollisionType.Tile) {
          enteredZoneRef.current = null;
          zone.onLeave && zone.onLeave();
        }
      });
    }

    const collisions = collisionResults.filter(
      ({ hit, zone }) => hit && zone.id !== targetId
    );

    const hitPageBarZones = collisions
      .filter(isPageBarZone)
      .map(({ zone }) => zone);

    const hitTileZones = collisions.filter(isTileZone).map(({ zone }) => zone);

    // schedule tile enter events if no page bar zones are hit and cursor has stopped
    if (!hitPageBarZones.length) {
      hitTileZones.forEach((zone) => {
        if (!zone.onEnter) return;
        // zone.onEnter(zone);

        const listenerId = pointerRef.current.addListener((pos) => {
          schedule(() => {
            pageBarLockRef.current = false;
            zone.onEnter({
              x: pos.x,
              y: pos.y,
            });
            enteredZoneRef.current = zone.id;
            pointerRef.current.removeListener(listenerId);
          });
        });
      });
    }

    // schedule enter events after pointer stops moving
    hitPageBarZones.forEach((zone) => {
      if (!zone.onEnter) return;
      const position = JSON.parse(JSON.stringify(pointerRef.current));

      // only schedule if change page delay has passed after initial call
      if (!pageBarLockRef.current) {
        pageBarLockRef.current = true;

        schedule(() => {
          zone.onEnter(position);
          if (pageBarLockRef.current === true)
            setTimeout(() => {
              pageBarLockRef.current = false;
              // enteredZoneRef.current = null;
            }, PAGE_CHANGE_DELAY);
        });
      }
    });
  }

  function getZone(id: string) {
    return dropZonesRef.current?.get(id);
  }

  function updateZone(details: any) {
    if (!details.ref) return;
    dropZonesRef.current?.set(details.id, details);
  }

  function removeZone(id: string) {
    dropZonesRef.current?.delete(id);
  }

  return {
    dropZonesRef,
    getZone,
    updateZone,
    removeZone,
  };
};

const isTileZone = (zone: CollisionResult) => zone.type === CollisionType.Tile;

const isPageBarZone = (zone: CollisionResult) =>
  zone.type === CollisionType.ChangePageBar;
